import {api} from 'api';
import axios from 'axios';
import {useRenameDialog} from 'components/dialogs/useRenameDialog';
import {Msg} from 'components/notifications/NotificationContext';
import {merge, without} from 'lodash';
import React, {useCallback, useState} from 'react';
import {FileData} from 'types';
import {buildPath} from 'utils';
import {useMove} from './useMove';

export type CreateFolderContext = ReturnType<typeof useCreateFolderContext>;
export type MoveContext = ReturnType<typeof useMove>;

export const useCreateFolderContext = (
   setItems: React.Dispatch<React.SetStateAction<FileData | null>>,
   refetchFolder: () => void,
   currentFolder: FileData | null
) => {
   const renameFolder = useCallback(
      async (file: FileData, name: string) => {
         if (name === file.name) {
            // setRenameId(null);
            return;
         }

         try {
            if (file.isNew) {
               await axios.put(`${api.createFolder}`, {path: buildPath(currentFolder!.path, name)});
               Msg.notify(`"${name}" folder has been created.`, 'success');
            } else {
               await axios.put(`${api.rename}`, {
                  from: buildPath(currentFolder!.path, file.name),
                  to: buildPath(currentFolder!.path, name),
               });
               Msg.notify(`"${file.name}" has been renamed to "${name}".`, 'success');
            }
            refetchFolder();
         } catch (e) {
            console.debug('rename error ', e);
            Msg.notify('Failed to remove: ' + (e as Error).message, 'error');
         }

         // setRenameId(null);
      },
      [setItems, currentFolder?.path, refetchFolder]
   );

   const {RenameDialogComponent, openRename} = useRenameDialog({
      files: currentFolder?.children!,
      onRename: renameFolder,
   });

   const cancelFolder = useCallback(
      (file: FileData) => {
         console.debug('Cancel creating', file);
         if (file.isNew) {
            setItems((prev) => {
               const items = prev?.children?.filter((f) => f.id !== file.id);
               return {...(prev ?? ({} as FileData)), children: items};
            });
         }
         // setRenameId(null);
      },
      [setItems]
   );

   const createNewFolder = useCallback(() => {
      const newFolder: FileData = {
         id: '-' + crypto.randomUUID(), // '-99',
         name: '',
         type: 'folder',
         path: '',
         isNew: true,
      };
      setItems((prev) =>
         merge({}, prev, {children: [newFolder, ...(prev?.children || [])]} as FileData)
      );
      openRename(newFolder, {title: 'Create', onClose: cancelFolder});
   }, [setItems, openRename, cancelFolder]);

   return {
      RenameDialogComponent,
      openRename,
      createNewFolder,
      // renameFolder,
      // cancelFolder,
   };
};
